// waterSpread.js
import chroma from 'chroma-js';
import { brushDefaults } from './helpers/brushLoader';
import { fillGradient } from './helpers/gradient';
import { getStrokeBounds, translatePoints, calculatePathDistances, calculatePath, interpolatePointsUniform } from './helpers/points';

// ==========================================
// 1. НАСТРОЙКИ КИСТИ (Defaults)
// ==========================================
const defaultBrushSettings = brushDefaults.waterSpread;

// ==========================================
// 2. ГЕНЕРАТОР ШУМА (Fractal Noise)
// ==========================================
class FractalNoise {
  constructor(seed = 1) {
    this.seed = seed;
    this.perm = new Uint8Array(512);
    this.grad = new Float32Array(512);
    this.init();
  }

  init() {
    let s = this.seed;
    const next = () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return (s >>> 0) / 4294967296;
    };

    for (let i = 0; i < 256; i++) {
      this.perm[i] = i;
      this.grad[i] = next() * 2 - 1;
    }
    
    for (let i = 255; i > 0; i--) {
      const r = Math.floor(next() * (i + 1));
      [this.perm[i], this.perm[r]] = [this.perm[r], this.perm[i]];
    }
    for (let i = 0; i < 256; i++) {
      this.perm[256 + i] = this.perm[i];
      this.grad[256 + i] = this.grad[i];
    }
  }

  lerp(a, b, t) { return a + t * (b - a); }
  fade(t) { return t * t * t * (t * (t * 6 - 15) + 10); }

  noise(x) {
    const X = Math.floor(x) & 255;
    const fx = x - Math.floor(x);
    const u = this.fade(fx);
    const a = this.perm[X];
    const b = this.perm[X + 1];
    return this.lerp(this.grad[a], this.grad[b], u);
  }

  fractal(x, octaves = 3, persistence = 0.5) {
    let total = 0;
    let frequency = 1;
    let amplitude = 1;
    let maxValue = 0;
    
    for(let i = 0; i < octaves; i++) {
        total += this.noise(x * frequency) * amplitude;
        maxValue += amplitude;
        amplitude *= persistence;
        frequency *= 2;
    }
    
    return total / maxValue;
  }
}

function seedCombine(...nums) {
  let s = 0x9e3779b9 >>> 0;
  for (let i = 0; i < nums.length; i++) {
    s = ((s ^ (nums[i] | 0)) * 0x9e3779b1) >>> 0;
  }
  return s >>> 0;
}

// ==========================================
// 3. ГЕОМЕТРИЯ И ТРАЕКТОРИЯ
// ==========================================

function precomputeTrajectory(pathPts, lineWidth, totalPathLen, settings) {
  const n = pathPts.length;
  if (n === 0) return [];
  const out = new Array(n);

  const { edgeStep, taperStart, taperEnd, taperPressure, capWidth } = settings;
  const getPt = (idx) => pathPts[Math.max(0, Math.min(n - 1, idx))];

  for (let i = 0; i < n; i++) {
    const p = pathPts[i];
    
    // 1. Нормали
    const pPrev = getPt(i - edgeStep);
    const pNext = getPt(i + edgeStep);
    let dx = pNext.x - pPrev.x;
    let dy = pNext.y - pPrev.y;
    if (dx === 0 && dy === 0) { dx = 1; dy = 0; } 

    const angle = Math.atan2(dy, dx);
    const nx = Math.cos(angle + Math.PI / 2);
    const ny = Math.sin(angle + Math.PI / 2);
    const tx = Math.cos(angle);
    const ty = Math.sin(angle);

    // 2. Tapering (Сужение)
    let taperFactor = 1.0;
    const distStart = p.distance;
    const distEnd = totalPathLen - p.distance;

    // Расчет длин сужения
    const startLen = Math.min(totalPathLen * 0.5, Math.max(lineWidth, totalPathLen * taperStart));
    const endLen = Math.min(totalPathLen * 0.5, Math.max(lineWidth, totalPathLen * taperEnd));

    // Логика сужения
    if (endLen > 0 && distEnd < endLen) {
        // Сужение в конце (работает всегда)
        const t = distEnd / endLen;
        const ease = t * (2 - t);
        taperFactor = capWidth + (1.0 - capWidth) * ease;
    } else if (settings.taperStartEnabled && startLen > 0 && distStart < startLen) {
        // Сужение в начале (ТОЛЬКО если включена опция)
        const t = distStart / startLen;
        const ease = t * (2 - t);
        taperFactor = capWidth + (1.0 - capWidth) * ease;
    }

    const pressure = p.pressure !== undefined ? p.pressure : 1;
    const combinedScale = taperFactor * (1 - taperPressure + pressure * taperPressure);

    out[i] = {
      x: p.x, 
      y: p.y,
      nx, ny, tx, ty,
      scale: Math.max(0.01, combinedScale),
      dist: p.distance,
      distFromEnd: p.distanceFromEnd,
      pressure: pressure
    };
  }
  return out;
}

/**
 * Генератор геометрии закругленного конца.
 * Теперь поддерживает полноценное заполнение (Flow) и обводку (Edge).
 */
function generateCapPoints(centerPt, isStart, layerPos, layerType, lineWidth, settings) {
  const points = [];
  const { capDetail } = settings;
  
  // Базовый расчет радиуса
  let radius = (lineWidth / 2) * centerPt.scale;

  // Логика размера начальной шапочки
  if (isStart) {
      if (!settings.taperStartEnabled) {
          // НОВОЕ ПОВЕДЕНИЕ: Если сужения нет, ограничиваем радиус текущей длиной штриха,
          // чтобы избежать визуального скачка "большого шара" в начале.

          const maxRadius = centerPt.pressure ?
          lineWidth * 0.5 * (1 - settings.taperPressure + centerPt.pressure * settings.taperPressure) :
          lineWidth * 0.5;


          const baseRadius = lineWidth * 0.5 * settings.capWidth;


          const lengthLimitRadius = baseRadius + Math.max(0.5, centerPt.distFromEnd * Math.max(settings.capWidth, 0.1));

          radius = Math.min(Math.max(baseRadius, lengthLimitRadius), maxRadius);
      }
      // Если taperStartEnabled === true, то centerPt.scale уже маленький,
      // и radius сам будет корректно маленьким, ничего делать не нужно.
  }
  
  // Векторы
  const nx = centerPt.nx; 
  const ny = centerPt.ny;
  const tx = centerPt.tx; 
  const ty = centerPt.ty;

  // Вектор "вглубь" шапочки (от тела к кончику)
  // Для Start: тело идет вперед, значит кончик назад (-tx, -ty).
  // Для End: тело пришло сюда, кончик дальше вперед (tx, ty).
  const tipDirX = isStart ? -tx : tx;
  const tipDirY = isStart ? -ty : ty;

  // ============================================================
  // ЛОГИКА 1: ВНУТРЕННИЕ ПОТОКИ (FLOW) - ЗАПОЛНЕНИЕ ПОЛУКРУГА
  // ============================================================
  if (layerType === 'flow') {
    // Мы не просто ставим точку. Мы должны сгенерировать линию от границы тела до края полукруга.
    
    // 1. Насколько глубоко этот конкретный слой заходит в полукруг?
    // Используем уравнение круга: depth = sqrt(R^2 - offset^2)
    // layerPos (-1..1) - это смещение по ширине.
    const relativeOffset = Math.abs(layerPos);
    // Если offset > 1 (баг), ограничиваем
    const safeOffset = Math.min(1.0, relativeOffset);
    
    // Длина хорды (глубина проникновения в шапочку)
    const maxDepth = radius * Math.sqrt(1 - safeOffset * safeOffset);
    
    // Если глубина слишком мала (самый край), ставим одну точку и выходим
    if (maxDepth < 0.5) {
         points.push({
            x: centerPt.x + nx * radius * layerPos,
            y: centerPt.y + ny * radius * layerPos,
            nx, ny,
            dist: isStart ? 0 : centerPt.dist, // Стык с телом
            scale: centerPt.scale,
            isCap: true
         });
         return points;
    }

    // 2. Генерируем точки линии
    // Нам нужно 3-5 точек, чтобы на них успел сыграть "wobble" (шум)
    const steps = Math.max(2, Math.ceil(capDetail / 2));

    for (let i = 0; i <= steps; i++) {
        const t = i / steps; // 0..1
        
        // Определяем текущую глубину и direction
        // Для START: мы рисуем ОТ кончика К телу (чтобы порядок точек совпадал с рисованием линии)
        // Для END: мы рисуем ОТ тела К кончику
        
        let currentDistOffset = 0; // Смещение дистанции для шума
        let currentDepth = 0;      // Физическое смещение от центральной точки

        if (isStart) {
            // Start Sequence: Tip -> Body
            // t=0 (Tip), t=1 (Body)
            currentDepth = maxDepth * (1 - t);
            // Чтобы шум был непрерывным, dist должен идти из минуса к 0
            currentDistOffset = -currentDepth;
        } else {
            // End Sequence: Body -> Tip
            // t=0 (Body), t=1 (Tip)
            currentDepth = maxDepth * t;
            // dist продолжает расти от конца линии
            currentDistOffset = currentDepth;
        }

        // Базовая позиция (сдвиг вбок по нормали + сдвиг вперед/назад к кончику)
        const baseX = centerPt.x + (nx * radius * layerPos);
        const baseY = centerPt.y + (ny * radius * layerPos);

        const finalX = baseX + (tipDirX * currentDepth);
        const finalY = baseY + (tipDirY * currentDepth);

        points.push({
            x: finalX,
            y: finalY,
            nx: nx, // Нормаль для шума остается боковой (чтобы вихляло поперек линии)
            ny: ny,
            dist: centerPt.dist + currentDistOffset, // Непрерывный dist для шума!
            scale: centerPt.scale,
            isCap: true
        });
    }
    return points;
  }

  // ============================================================
  // ЛОГИКА 2: КРАЯ (EDGE) - ОБВОДКА ПОЛУКРУГА
  // ============================================================
  if (layerType === 'edge') {
    const isRightEdge = layerPos > 0;
    
    // Вектор боковины (откуда начинаем или где заканчиваем дугу)
    const sideNx = isRightEdge ? nx : -nx;
    const sideNy = isRightEdge ? ny : -ny;
    
    const steps = capDetail;
    
    // Для краев мы интерполируем вектор нормали, создавая дугу.
    for (let i = 0; i <= steps; i++) {
      const t = i / steps; 
      
      let cx, cy; // Вектор направления от центра к поверхности шапочки
      
      // Slerp-подобная интерполяция между Боковиной и "Затылком" (tipDir)
      if (isStart) {
        // Start: Tip -> Side (0 -> 1)
        // Tip (tipDir) ... Side (sideN)
        cx = (1 - t) * tipDirX + t * sideNx;
        cy = (1 - t) * tipDirY + t * sideNy;
      } else {
        // End: Side -> Tip (0 -> 1)
        cx = (1 - t) * sideNx + t * tipDirX;
        cy = (1 - t) * sideNy + t * tipDirY;
      }
      
      // Нормализуем, чтобы получить идеальную дугу
      const len = Math.sqrt(cx * cx + cy * cy);
      if (len > 0.001) { cx /= len; cy /= len; }

      // Расчет dist для шума (длина дуги)
      // Четверть длины окружности = PI * R / 2
      const arcLen = (Math.PI * radius) / 2;
      
      let d = 0;
      if (isStart) {
        // t=0 (Tip, dist = -arcLen) -> t=1 (Side, dist = 0)
        d = -arcLen * (1 - t);
      } else {
        // t=0 (Side, dist = Total) -> t=1 (Tip, dist = Total + arcLen)
        d = centerPt.dist + arcLen * t;
      }

      points.push({
        x: centerPt.x + cx * radius,
        y: centerPt.y + cy * radius,
        nx: cx, // Здесь нормаль радиальная (перпендикулярно касательной дуги)
        ny: cy,
        dist: d,
        scale: centerPt.scale, // Важно передать скейл
        isCap: true
      });
    }
  }
  
  return points;
}

// ==========================================
// 4. ГЕНЕРАТОР СЛОЕВ АКВАРЕЛИ
// ==========================================

function generateWatercolorLayers(lineWidth, settings, time) {
  const seedBase = Math.floor(time * 10000);
  const layers = [];
  
  const { 
    edgeConcentration, 
    edgeWidth, 
    edgeWobbleScale, 
    edgeWobbleAmount,
    wetness,
    bleeding
  } = settings;
  
  // --- Внутренние потоки (Flow) ---
  let flowCount = Math.max(3, Math.floor(lineWidth / 4)); 

  if (lineWidth <= 50) {
    const coef = Math.max(1, 50 / lineWidth);
    flowCount = Math.min(10, Math.floor(lineWidth / 4 * coef) ); 
  }
  
  for (let i = 0; i < flowCount; i++) {
    const t = (i / (flowCount - 1)) * 2 - 1; // -1 ... 1
    const proximityToCenter = 1 - Math.abs(t);
    
    const layerSeed = seedCombine(seedBase, i);
    const noiseGen = new FractalNoise(layerSeed);
    
    let alpha = 0.05 + (0.2 * (1 - proximityToCenter) * edgeConcentration);
    if (wetness > 0.5) alpha *= (1.5 - wetness);
    alpha *= (2.5 / Math.sqrt(flowCount)); 

    layers.push({
      type: 'flow',
      pos: t, 
      widthFactor: (1.0 / flowCount) * 2.5,
      alpha: Math.min(1, alpha),
      noiseFreq: 0.1, 
      noiseAmp: bleeding * lineWidth * 0.5, 
      noiseGen: noiseGen
    });
  }

  // --- Границы (Edges) ---
  const sides = [-1, 1];
  sides.forEach((side, idx) => {
    const noiseGen = new FractalNoise(seedCombine(seedBase, 100 + idx));
    const pxWidth = Math.max(1.0, edgeWidth * (1 + Math.min(5, lineWidth / 80))); 
    
    layers.push({
      type: 'edge',
      pos: side * 0.98, 
      widthFactor: 0,
      fixedWidth: pxWidth, 
      alpha: Math.min(1, 0.5 * edgeConcentration), 
      noiseFreq: edgeWobbleScale, 
      noiseAmp: edgeWobbleAmount * lineWidth * 0.2,
      noiseGen: noiseGen
    });
  });

  return layers;
}


// ==========================================
// 5. ОСНОВНАЯ ФУНКЦИЯ РИСОВАНИЯ
// ==========================================

export async function drawWaterSpreadStroke({
  color,
  gradientColor,
  lineWidth,
  points,
  time,
  sets = {},
}, context) {
  if (!points || points.length < 2) return;

  const settings = Object.assign({}, defaultBrushSettings, sets);
  
  // 1. Подготовка геометрии
  const bounds = getStrokeBounds(points, lineWidth * 2);
  if (!bounds) return;

  const localPoints = translatePoints(points, -bounds.x, -bounds.y);
  const smoothPoints = interpolatePointsUniform(localPoints, Math.max(3, lineWidth / 10));
  const totalPathLen = calculatePath(smoothPoints);
  const pathPoints = calculatePathDistances(smoothPoints);
  
  const trajectory = precomputeTrajectory(pathPoints, lineWidth, totalPathLen, settings);
  if (trajectory.length < 2) return;

  // 2. Буфер
  const bufferCanvas = document.createElement('canvas');
  bufferCanvas.width = bounds.width;
  bufferCanvas.height = bounds.height;
  const ctx = bufferCanvas.getContext('2d');
  ctx.lineCap = 'round'; 
  ctx.lineJoin = 'round';

  // 3. Генерация слоев
  const layers = generateWatercolorLayers(lineWidth, settings, time);

  // 4. Сбор путей (Batching)
  const batchParams = [];

  layers.forEach(layer => {
    const path = new Path2D();
    let isMoved = false;
    let lastPoint = null; // Хранилище для предыдущей точки

    // -- Хелпер для добавления точки --
    const addPointToPath = (pt, isCapPoint) => {
        let nx, ny;
        // Шум
        // Используем pt.scale (который уже учитывает tapering)
        // Для flow внутри шапочки scale такой же, как на конце тела.
        const noiseVal = layer.noiseGen.fractal(pt.dist * layer.noiseFreq, 2, 0.5);
        const offset = noiseVal * layer.noiseAmp * pt.scale;

        if (isCapPoint) {
            // Для Cap точки (x, y) уже вычислены геометрически верно.
            // Мы просто сдвигаем их по нормали.
            nx = pt.x + pt.nx * offset;
            ny = pt.y + pt.ny * offset;
        } else {
            // Для Body точки считаем базу от центральной линии

            // Если включено сужение начала - учитываем dist (расстояние от старта),
            // иначе - только distFromEnd (расстояние до конца).
            const distLimit = settings.taperStartEnabled
                ? Math.min(pt.dist, pt.distFromEnd)
                : pt.distFromEnd;

            const baseWidth = Math.min(
              (lineWidth / 2) * pt.scale,
              (distLimit + (lineWidth / 2)) * Math.max(settings.capWidth, 0.1)
            );

            nx = pt.x + pt.nx * baseWidth * layer.pos + pt.nx * offset;
            ny = pt.y + pt.ny * baseWidth * layer.pos + pt.ny * offset;
        }

        if (!isMoved) {
            path.moveTo(nx, ny);
            isMoved = true;
            lastPoint = { x: nx, y: ny };
        } else {
            // СГЛАЖИВАНИЕ: Используем quadraticCurveTo к середине отрезка
            const midX = (lastPoint.x + nx) / 2;
            const midY = (lastPoint.y + ny) / 2;
            path.quadraticCurveTo(lastPoint.x, lastPoint.y, midX, midY);

            // Обновляем lastPoint на текущую (она станет контрольной для следующего сегмента)
            lastPoint = { x: nx, y: ny };
        }
    };

    // A. Start Cap (генерирует массив точек от кончика до стыка)
    const startCapPoints = generateCapPoints(trajectory[0], true, layer.pos, layer.type, lineWidth, settings);
    startCapPoints.forEach(pt => addPointToPath(pt, true));

    // B. Body
    const step = layer.type === 'edge' ? 1 : 1;
    for (let i = 0; i < trajectory.length; i += step) {
        addPointToPath(trajectory[i], false);
    }

    // C. End Cap (генерирует массив точек от стыка до кончика)
    const endCapPoints = generateCapPoints(trajectory[trajectory.length - 1], false, layer.pos, layer.type, lineWidth, settings);
    endCapPoints.forEach(pt => addPointToPath(pt, true));

    // ВАЖНО: Доводим линию до самой последней точки
    if (lastPoint) {
        path.lineTo(lastPoint.x, lastPoint.y);
    }

    // Параметры отрисовки
    const currentLineWidth = layer.fixedWidth
       ? layer.fixedWidth * Math.min(1, lineWidth/10)
       : Math.max(0.5, lineWidth * layer.widthFactor);

    batchParams.push({
      path: path,
      alpha: layer.alpha,
      width: currentLineWidth
    });
  });

  // 5. Рендеринг
  const chromaC = chroma(color);
  const baseRgb = chromaC.rgb();
  const globalOpacity = settings.opacity * chromaC.alpha();

  batchParams.forEach(batch => {
    const finalAlpha = Math.min(1, batch.alpha * globalOpacity * 6);
    const layerColor = `rgba(${baseRgb[0]},${baseRgb[1]},${baseRgb[2]},${finalAlpha})`;
    
    ctx.strokeStyle = layerColor;
    ctx.lineWidth = batch.width;
    ctx.stroke(batch.path);
  });

  // 6. Композиция
  if (gradientColor) {
    fillGradient(ctx, smoothPoints, color, gradientColor, lineWidth);
    context.globalAlpha = 1;
  } else {
    context.globalAlpha = 1;
  }

  context.drawImage(
    bufferCanvas,
    0, 0, bounds.width, bounds.height,
    bounds.x, bounds.y, bounds.width, bounds.height
  );
}